import { Controller } from '@hotwired/stimulus'
import interact from 'interactjs'

export default class extends Controller {
  static targets = ['divider', 'shell']
  static values = {
    minWidth: { type: Number, default: 250 },
    maxWidth: { type: Number, default: 1250 },
    storageKey: { type: String, default: 'chatFrameWidth' },
  }

  connect() {
    this.debounceTimer = null
    this.boundHandleResize = this.handleResize.bind(this)
    this.pendingWidthUpdate = null
    this.rafId = null
    this.lastAppliedWidth = null

    this.loadSavedWidth()
    this.setupInteract()

    // Listen for window resize to restore saved width when window becomes wide enough
    window.addEventListener('resize', this.boundHandleResize)
  }

  disconnect() {
    window.removeEventListener('resize', this.boundHandleResize)
    this.cleanup()
  }

  handleResize() {
    // If window becomes wide enough, restore saved width if we haven't set it yet
    if (window.innerWidth >= 1250 && !this.hasLoadedSavedWidth) {
      this.loadSavedWidth()
      this.hasLoadedSavedWidth = true
    } else if (window.innerWidth < 1250) {
      this.hasLoadedSavedWidth = false
    }
  }

  setupInteract() {
    const divider = this.dividerTarget
    if (!divider) return

    this.interactInstance = interact(divider)
      .draggable({
        axis: 'x',
        listeners: {
          start: (event) => this.handleDragStart(event),
          move: (event) => this.handleDragMove(event),
          end: (event) => this.handleDragEnd(event),
        },
      })
      .on('dragstart', (event) => {
        // Don't allow resizing on mobile or when window is too narrow
        if (window.innerWidth < 1250) {
          event.preventDefault()
          return false
        }
      })

    // Add fallback mouse/pointer event listeners to ensure drag ends properly
    this.boundHandleMouseUp = this.forceDragEnd.bind(this)
    document.addEventListener('mouseup', this.boundHandleMouseUp, {
      passive: true,
    })
    document.addEventListener('mouseleave', this.boundHandleMouseUp, {
      passive: true,
    })
    document.addEventListener('pointerup', this.boundHandleMouseUp, {
      passive: true,
    })
  }

  cleanup() {
    if (this.interactInstance) {
      this.interactInstance.unset()
      this.interactInstance = null
    }

    if (this.boundHandleMouseUp) {
      document.removeEventListener('mouseup', this.boundHandleMouseUp)
      document.removeEventListener('mouseleave', this.boundHandleMouseUp)
      document.removeEventListener('pointerup', this.boundHandleMouseUp)
    }

    // Remove overlay if still present
    this.removeDragOverlay()

    // Force end any active drag
    if (this.isDragging) {
      this.forceDragEnd()
    }

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
  }

  forceDragEnd() {
    if (this.isDragging) {
      this.handleDragEnd({})
    }
  }

  loadSavedWidth() {
    // Only load saved width if window is wide enough for side-by-side layout
    if (window.innerWidth < 1250) {
      this.hasLoadedSavedWidth = false
      return
    }

    this.hasLoadedSavedWidth = true

    const savedWidth = localStorage.getItem(this.storageKeyValue)
    if (savedWidth) {
      const width = parseInt(savedWidth, 10)
      if (
        !isNaN(width) &&
        width >= this.minWidthValue &&
        width <= this.maxWidthValue
      ) {
        this.setChatWidth(width)
        return
      }
    }

    // Use responsive defaults
    const defaultWidth = window.innerWidth >= 1400 ? 350 : 250
    this.setChatWidth(defaultWidth)
  }

  handleDragStart(event) {
    if (window.innerWidth < 1250) {
      return
    }

    const shell = this.shellTarget || this.element
    const shellRect = shell.getBoundingClientRect()
    const dividerRect = event.target.getBoundingClientRect()

    this.startShellRight = shellRect.right
    this.startWidth = this.getCurrentChatWidth()
    // Track where the divider started (center point)
    this.startDividerX = dividerRect.left + dividerRect.width / 2
    this.startMouseX = event.clientX || event.pageX
    this.lastAppliedWidth = this.startWidth
    this.isDragging = true

    // Cancel any pending updates
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }

    // Create overlay for smooth preview
    this.createDragOverlay(shell)
  }

  createDragOverlay(shell) {
    // Store original position for calculations
    const shellRect = shell.getBoundingClientRect()

    // Create a fixed full-screen overlay
    const overlay = document.createElement('div')
    overlay.id = 'resize-preview-overlay'
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      z-index: 9999;
      pointer-events: none;
      background: rgba(0, 0, 0, 0.3);
    `

    // Create preview container positioned exactly where the shell is
    const previewShell = document.createElement('div')
    previewShell.className = 'resize-preview-shell'
    previewShell.style.cssText = `
      position: absolute;
      left: ${shellRect.left}px;
      top: ${shellRect.top}px;
      width: ${shellRect.width}px;
      height: ${shellRect.height}px;
      display: grid;
      grid-template-columns: 1fr auto var(--chat-width, ${this.startWidth}px);
      pointer-events: auto;
      cursor: col-resize;
      background: transparent;
      overflow: hidden;
    `

    // Copy the chat width from shell
    const shellStyles = window.getComputedStyle(shell)
    previewShell.style.setProperty(
      '--chat-width',
      shellStyles.getPropertyValue('--chat-width'),
    )

    // Create preview panes
    const leftPane = document.createElement('div')
    leftPane.style.cssText = `
      background: transparent;
      border-right: 1px solid #ddd;
    `

    const dividerPreview = document.createElement('div')
    dividerPreview.style.cssText = `
      width: 4px;
      background-color: #EEE;
      cursor: col-resize;
    `

    const rightPane = document.createElement('div')
    rightPane.style.cssText = `
      background: transparent;
      border-left: 1px solid #ddd;
    `

    previewShell.appendChild(leftPane)
    previewShell.appendChild(dividerPreview)
    previewShell.appendChild(rightPane)

    // Add divider indicator (blue line showing where divider is)
    const dividerIndicator = document.createElement('div')
    dividerIndicator.className = 'resize-divider-indicator'
    dividerIndicator.style.cssText = `
      position: absolute;
      width: 4px;
      background-color: rgba(59, 130, 246, 0.8);
      height: ${shellRect.height}px;
      pointer-events: none;
      z-index: 10000;
      top: ${shellRect.top}px;
      box-shadow: 0 0 4px rgba(59, 130, 246, 0.5);
    `

    overlay.appendChild(previewShell)
    overlay.appendChild(dividerIndicator)

    document.body.appendChild(overlay)

    this.overlay = overlay
    this.previewShell = previewShell
    this.dividerIndicator = dividerIndicator

    // Store original position for calculations
    this.overlayShellRect = {
      left: shellRect.left,
      right: shellRect.right,
      top: shellRect.top,
      bottom: shellRect.bottom,
      height: shellRect.height,
    }
  }

  handleDragMove(event) {
    if (window.innerWidth < 1250 || !this.isDragging || !this.overlay) {
      return
    }

    // Prevent interact.js from applying transform (grid layout handles positioning)
    event.target.style.transform = 'none'

    // Use overlay shell rect instead of recalculating
    const shellRight = this.overlayShellRect.right

    // Get current mouse position
    const currentX = event.clientX || event.pageX

    // Calculate where the divider should be based on mouse movement
    // The divider position = startDividerX + (currentMouseX - startMouseX)
    const dividerX = this.startDividerX + (currentX - this.startMouseX)

    // Calculate new width: shellRight - dividerX
    // Width represents the width of the chat panel (right side)
    const newWidth = shellRight - dividerX

    // Constrain to min/max (250px - 1250px)
    const constrainedWidth = Math.max(
      this.minWidthValue,
      Math.min(this.maxWidthValue, newWidth),
    )

    // Adjust divider position if width was constrained
    // If width hit min boundary, divider should be at max position
    // If width hit max boundary, divider should be at min position
    const constrainedDividerX =
      constrainedWidth === this.minWidthValue
        ? shellRight - this.minWidthValue
        : constrainedWidth === this.maxWidthValue
          ? shellRight - this.maxWidthValue
          : dividerX

    // Update preview overlay instead of real content
    this.updatePreviewWidth(constrainedWidth, constrainedDividerX)

    // Store for final application
    this.pendingWidthUpdate = constrainedWidth
  }

  updatePreviewWidth(width, dividerX) {
    if (!this.previewShell || !this.dividerIndicator) return

    // Update preview shell grid column width
    this.previewShell.style.setProperty('--chat-width', `${width}px`)
    this.previewShell.style.gridTemplateColumns = `1fr auto ${width}px`

    // Update divider indicator position (absolute to viewport)
    this.dividerIndicator.style.left = `${dividerX - 2}px`
  }

  handleDragEnd(event) {
    if (!this.isDragging) {
      return
    }

    if (window.innerWidth < 1250) {
      this.removeDragOverlay()
      this.isDragging = false
      return
    }

    this.isDragging = false

    // Cancel any pending RAF updates
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }

    // Get final width from pending update or calculate
    let constrainedWidth = this.pendingWidthUpdate

    if (constrainedWidth === null) {
      // Fallback calculation if pending update is missing
      const shell = this.shellTarget || this.element
      const shellRect = shell.getBoundingClientRect()
      const shellRight = shellRect.right
      const finalX = event?.clientX || event?.pageX || this.startMouseX
      const finalDividerX = this.startDividerX + (finalX - this.startMouseX)
      const finalWidth = shellRight - finalDividerX
      constrainedWidth = Math.max(
        this.minWidthValue,
        Math.min(this.maxWidthValue, finalWidth),
      )
    }

    // Remove overlay first
    this.removeDragOverlay()

    // Apply the final width to the real content
    this.setChatWidthDirect(constrainedWidth)
    this.lastAppliedWidth = constrainedWidth

    // Ensure no transform is applied
    if (event && event.target) {
      event.target.style.transform = 'none'
    }

    // Save immediately on drag end
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    this.saveWidth(constrainedWidth)
  }

  removeDragOverlay() {
    if (this.overlay) {
      this.overlay.remove()
      this.overlay = null
      this.previewShell = null
      this.dividerIndicator = null
    }
  }

  getCurrentChatWidth() {
    const shell = this.shellTarget || this.element
    const computedStyle = getComputedStyle(shell)
    const chatWidth = computedStyle.getPropertyValue('--chat-width').trim()
    return parseInt(chatWidth, 10) || 250
  }

  setChatWidth(width) {
    // Legacy method - use setChatWidthDirect for better performance
    this.setChatWidthDirect(width)
  }

  setChatWidthDirect(width) {
    const shell = this.shellTarget || this.element
    shell.style.setProperty('--chat-width', `${width}px`)
  }

  debounceSaveWidth(width) {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    this.debounceTimer = setTimeout(() => {
      this.saveWidth(width)
    }, 5000)
  }

  saveWidth(width) {
    if (window.innerWidth >= 1250) {
      localStorage.setItem(this.storageKeyValue, width.toString())
    }
  }
}
